<template>
  <div
    class="date-range-picker__dropdown-calendar-wrapper"
  >
    <div class="date-range-picker__calendars">
      <div class="date-range-picker__calendar date-range-picker__calendar_left">
        <div
          v-if="twoCalendars"
          class="date-range-picker__calendar_name"
        >
          {{ _getTr('start_date') }}
        </div>
        <Calendar
          :month-date="firstCalendarMonth"
          :locale="locale"
          :start-date="startDate"
          :end-date="firstCalendarEndDate"
          :min-date="minDate"
          :max-date="firstCalendarMaxDate"
          :arrow-left="true"
          :arrow-right="twoCalendars"
          :is-prev-month-available="isFirstCalendarPrevMonthAvailable"
          :is-next-month-available="isFirstCalendarNextMonthAvailable"
          @nextMonth="firstCalendarNextMonth"
          @prevMonth="firstCalendarPrevMonth"
          @change-month="changeLeftMonth"
          @dateClick="$emit('input', {value: $event, order: 'start'})"
        />
      </div>

      <div
        class="date-range-picker__calendar date-range-picker__calendar_right"
      >
        <div
          v-if="twoCalendars"
          class="date-range-picker__calendar_name"
        >
          {{ _getTr('end_date') }}
        </div>
        <Calendar
          :month-date="secondCalendarMonth"
          :locale="locale"
          :start-date="secondCalendarStartDate"
          :end-date="endDate"
          :min-date="secondCalendarMinDate"
          :max-date="maxDate"
          :arrow-left="twoCalendars"
          :arrow-right="true"
          :is-next-month-available="isSecondCalendarNextMonthAvailable"
          :is-prev-month-available="isSecondCalendarPrevMonthAvailable"
          @nextMonth="secondCalendarNextMonth"
          @prevMonth="secondCalendarPrevMonth"
          @change-month="changeRightMonth"
          @dateClick="$emit('input', {value: $event, order: 'end'})"
        />
      </div>
    </div>

    <div
      v-if="!areRangePresetsHidden && ranges"
      class="date-range-picker__ranges"
    >
      <TagList
        :tags="rangeTags"
        :value="tagListValue"
        :enable-toggling="false"
        :all-button="false"
        :allow-multiple-select="false"
        small
        gray
        @input="$emit('clickRange', $event)"
      />
    </div>

    <div class="date-range-picker__buttons">
      <button
        v-if="!hideClearButton"
        class="date-range-picker__button date-range-picker__button-reset"
        type="button"
        @click="$emit('clear')"
      >
        {{ locale.clearLabel }}
      </button>
      <button
        class="date-range-picker__button date-range-picker__button-success"
        @click="$emit('close')"
      >
        {{ locale.applyLabel }}
      </button>
    </div>
  </div>
</template>

<script>
  import Calendar from 'components/Inputs/Dates/common/Calendar';
  import TagList from 'components/Inputs/Select/Tags/TagList';
  import {
    isDayAfterOrEqual,
    isDayBeforeOrEqual,
    nextMonth,
    prevMonth,
    dateRangeTranslations,
    dateIsBetween
  } from 'components/Inputs/Dates/DateRangePicker/util';

  import moment from 'moment';
  import getTr from 'translation';

  export default {
    name: 'DropdownCalendar',
    components: {Calendar, TagList},
    props: {
      twoCalendars: {
        type: Boolean,
        default: false
      },
      minDate: {
        type: Date,
        default: null
      },
      maxDate: {
        type: Date,
        default: null
      },
      startDate: {
        type: Date,
        default: null
      },
      endDate: {
        type: Date,
        default: null
      },
      monthDate: {
        type: Date,
        default: null
      },
      areRangePresetsHidden: {
        type: Boolean,
        default: false
      },
      small: {
        type: Boolean,
        default: true
      },
      locale: {
        type: Object,
        default: Object
      },
      ranges: {
        type: Object,
        default: null
      },
      activeRangeKey: {
        type: String,
        default: null
      },
      hideClearButton: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        internalFirstCalendarMonth: null,
        internalSecondCalendarMonth: null,
        rangeTranslations: dateRangeTranslations()
      };
    },
    computed: {
      firstCalendarEndDate () {
        if (this.twoCalendars) {
          return this.startDate;
        }
        return this.endDate;
      },
      firstCalendarMaxDate () {
        if (this.twoCalendars) {
          if (this.endDate) {
            return this.endDate;
          }
        }
        return this.maxDate;
      },
      secondCalendarStartDate () {
        if (this.twoCalendars) {
          return this.endDate;
        }
        return this.startDate;
      },
      secondCalendarMinDate () {
        if (this.twoCalendars) {
          if (this.startDate) {
            return this.startDate;
          }
        }
        return this.minDate;
      },
      firstCalendarMonth () {
        if (this.twoCalendars) {
          return this.internalFirstCalendarMonth;
        }
        return this.monthDate;
      },
      secondCalendarMonth () {
        if (this.twoCalendars) {
          return this.internalSecondCalendarMonth;
        }
        return this.nextMonthDate;
      },
      nextMonthDate () {
        return nextMonth(this.monthDate);
      },
      isFirstCalendarPrevMonthAvailable () {
        return this.checkPrevMonthAvailable(this.firstCalendarMonth);
      },
      isFirstCalendarNextMonthAvailable () {
        return this.checkNextMonthAvailable(this.firstCalendarMonth);
      },
      isSecondCalendarPrevMonthAvailable () {
        return this.checkPrevMonthAvailable(this.secondCalendarMonth);
      },
      isSecondCalendarNextMonthAvailable () {
        return this.checkNextMonthAvailable(this.secondCalendarMonth);
      },
      isPrevMonthAvailable () {
        return this.isFirstCalendarPrevMonthAvailable;
      },
      isNextMonthAvailable () {
        return this.isSecondCalendarNextMonthAvailable;
      },
      tagListValue () {
        if (this.activeRangeKey) {
          return [this.activeRangeKey];
        }
        return [];
      },
      rangeTags () {
        return Object.keys(this.ranges).map(key => {
          if (key === 'Last 7 days') {
            return {
              id: key,
              title: getTr('last_x_days').format(7),
              text: getTr('last_x_days').format(7)
            };
          }
          const text = this.rangeTranslations[key]();
          return {
            id: key,
            title: text,
            text
          };
        });
      }
    },
    created () {
      // for independent months in calendar
      this.internalFirstCalendarMonth = this.startDate ? this.startDate : new Date();
      this.internalSecondCalendarMonth = this.endDate ? this.endDate : new Date();
    },
    methods: {
      checkPrevMonthAvailable (date) {
        const lastDayPrevMonth = moment(date).startOf('month').
          subtract(1, 'days');

        // if minDate is not defined then we can go back in time as much as we want
        return !this.minDate || isDayAfterOrEqual(lastDayPrevMonth, this.minDate);
      },
      checkNextMonthAvailable (date) {
        const firstDayNextMonth = moment(date).startOf('month').
          add(1, 'months');

        return !this.maxDate || isDayBeforeOrEqual(firstDayNextMonth, this.maxDate);
      },
      limitMonth (newMonth) {
        if (!this.maxDate || !this.minDate) {
          return newMonth;
        } else if (dateIsBetween(newMonth, this.minDate, this.maxDate)) {
          return newMonth;
        } else if (moment(newMonth).isAfter(this.maxDate) || moment(newMonth).isSame(this.maxDate)) {
          return this.maxDate;
        }
        return this.minDate;
      },
      firstCalendarNextMonth () {
        if (this.twoCalendars) {
          let month = nextMonth(moment(this.firstCalendarMonth).startOf('month').
            toDate());
          month = this.limitMonth(month);
          this.internalFirstCalendarMonth = month;

          return;
        }

        this.nextMonth();
      },
      firstCalendarPrevMonth () {
        if (this.twoCalendars) {
          let month = prevMonth(moment(this.firstCalendarMonth).startOf('month').
            toDate());
          month = this.limitMonth(month);
          this.internalFirstCalendarMonth = month;

          return;
        }

        this.prevMonth();
      },
      changeLeftMonth (value) {
        const newDate = moment(value.year, value.month, 1).toDate();
        this.$emit('changeMonth', newDate);
      },
      changeRightMonth (value) {
        if (value.month <= 0) {
          value.month = 11;
          value.year -= 1;
        } else {
          value.month -= 1;
        }
        this.changeLeftMonth(value);
      },
      secondCalendarNextMonth () {
        if (this.twoCalendars) {
          let month = nextMonth(moment(this.secondCalendarMonth).startOf('month').
            toDate());
          month = this.limitMonth(month);
          this.internalSecondCalendarMonth = month;

          return;
        }

        this.nextMonth();
      },
      secondCalendarPrevMonth () {
        if (this.twoCalendars) {
          let month = prevMonth(moment(this.secondCalendarMonth).startOf('month').
            toDate());
          month = this.limitMonth(month);
          this.internalSecondCalendarMonth = month;

          return;
        }

        this.prevMonth();
      },
      nextMonth () {
        if (this.isNextMonthAvailable) {
          this.$emit('changeMonth', nextMonth(moment(this.monthDate).startOf('month').
            toDate()));
        }
      },
      prevMonth () {
        if (this.isPrevMonthAvailable) {
          this.$emit('changeMonth', prevMonth(moment(this.monthDate).startOf('month').
            toDate()));
        }
      }
    }
  };
</script>

<style>
  .date-range-picker__dropdown-calendar-wrapper {
    color: inherit;

    z-index: 3001;

    box-sizing: border-box;
    width: 476px;

    padding: 22px 24px 15px;

    margin-top: 2px;

    background-color: var(--background-color);

    border-radius: var(--border-radius);
  }

  .date-range-picker__calendars {
    display: flex;

    flex-direction: row;
    justify-content: space-between;
  }

  .date-range-picker__calendar_name {
    font-size: var(--secondary-font-size);

    color: var(--panel-text-color);

    text-align: center;
  }

  .date-range-picker__ranges {
    margin: 6px 0 0;
  }

  .date-range-picker__ranges .tag-list .tag-list__tag {
    margin-top: 8px;
  }

  .date-range-picker__buttons {
    line-height: 12px;

    text-align: right;
    vertical-align: middle;

    display: none;
    clear: both;

    padding: 20px 0 0;

    border: none;
  }

  .date-range-picker__button {
    cursor: pointer;
    margin-left: 18px;
  }

  .date-range-picker__button-reset,
  .date-range-picker__button-success {
    font-family: var(--main-font-family);
    font-size: var(--subtitle-font-size);
    font-weight: var(--medium-font-weight);

    padding-bottom: 0;

    background: transparent;

    border: none;

    outline: none;
  }

  .date-range-picker__button-reset {
    color: var(--gray-text-color);
  }

  .date-range-picker__button-success {
    color: var(--action-color);
  }

  .date-range-picker__button-success:hover {
    color: var(--text-link-blue-hover-color);
  }

  .date-range-picker__dropdown-calendar-wrapper,
  .date-range-picker__dropdown-calendar-wrapper .date-range-picker__calendar,
  .date-range-picker__dropdown-calendar-wrapper .date-range-picker__buttons {
    display: block;
  }
</style>
